import { authService } from '../services/auth';
import { billingApi, BillingOverviewDto, BillingPlanDto } from '../services/billing.api';
import { toastService } from '../services/toast';
import { onLocaleChange, t } from '../services/i18n';
import { formatApproxDocumentCount, formatBytes, formatMoney, formatPropertyLimit } from '../components/profile/profile-shared';

let initialized = false;
let cachedPublicPlans: BillingPlanDto[] = [];

function renderPublicPlans(container: HTMLElement, plans: BillingPlanDto[]): void {
  const starterPlan = plans.find((plan) => plan.key === 'starter');

  plans.forEach((plan) => {
    container.querySelectorAll<HTMLElement>(`[data-plan-name="${plan.key}"]`).forEach((el) => {
      el.textContent = plan.displayName;
    });

    container.querySelectorAll<HTMLElement>(`[data-plan-price="${plan.key}"]`).forEach((el) => {
      if (typeof plan.monthlyPriceInCents === 'number') {
        el.dataset.monthly = formatMoney(plan.monthlyPriceInCents, plan.currency || 'EUR');
      }

      if (typeof plan.yearlyPriceInCents === 'number') {
        el.dataset.yearly = formatMoney(plan.yearlyPriceInCents, plan.currency || 'EUR');
      }
    });

    container.querySelectorAll<HTMLElement>(`[data-plan-properties="${plan.key}"]`).forEach((el) => {
      el.textContent = formatPropertyLimit(plan.propertyLimit);
    });

    container.querySelectorAll<HTMLElement>(`[data-plan-storage="${plan.key}"]`).forEach((el) => {
      el.textContent = t('billing.storagePlanFeature', {
        storage: formatBytes(plan.storageLimitBytes),
        count: formatApproxDocumentCount(plan.storageLimitBytes),
      });
    });
  });

  if (starterPlan) {
    container.querySelectorAll<HTMLElement>('[data-plan-trial-storage]').forEach((el) => {
      el.textContent = t('billing.storagePlanFeature', {
        storage: formatBytes(starterPlan.storageLimitBytes),
        count: formatApproxDocumentCount(starterPlan.storageLimitBytes),
      });
    });
  }
}

function setResumeOverlayVisible(visible: boolean): void {
  document.documentElement.classList.toggle('pricing-checkout-resume', visible);
}

function redirectToBillingProcessing(): void {
  setResumeOverlayVisible(false);
  billingApi.clearPendingCheckout();
  toastService.warning(t('billing.checkoutProcessing'), 15000);
  window.location.href = '/profile/?tab=subscription&billing=processing';
}

function applyBillingInterval(container: HTMLElement, activeBilling: 'monthly' | 'yearly'): void {
  container.dataset.activeBilling = activeBilling;
  container.querySelectorAll('.pricing-toggle-btn').forEach(node => {
    node.classList.toggle('active', (node as HTMLButtonElement).dataset.billing === activeBilling);
  });

  container.querySelectorAll<HTMLElement>('.price-amount[data-monthly]').forEach(el => {
    el.textContent = activeBilling === 'yearly' ? el.dataset.yearly || '' : el.dataset.monthly || '';
  });

  container.querySelectorAll<HTMLElement>('.price-period[data-monthly-period]').forEach(el => {
    el.textContent = activeBilling === 'yearly' ? t(el.dataset.yearlyPeriod || '') : t(el.dataset.monthlyPeriod || '');
  });

  container.querySelectorAll<HTMLElement>('[data-subscribe-plan]').forEach(el => {
    el.setAttribute('data-billing-interval', activeBilling);
  });
}

async function loadPublicPlans(container: HTMLElement): Promise<void> {
  try {
    cachedPublicPlans = await billingApi.getPublicPlans();
    renderPublicPlans(container, cachedPublicPlans);
    applyBillingInterval(container, (container.dataset.activeBilling as 'monthly' | 'yearly') || 'monthly');
  } catch {
    // Leave the static fallback copy in place when the public plan catalog is temporarily unavailable.
  }
}

async function startCheckout(
  anchor: HTMLAnchorElement,
  hasManageableSubscription: boolean,
): Promise<void> {
  if (billingApi.getPendingBillingAction()) {
    redirectToBillingProcessing();
    return;
  }

  if (hasManageableSubscription) {
    billingApi.clearPendingCheckout();
    billingApi.clearPendingBillingAction();
    toastService.warning(t('billing.manageExistingSubscription'));
    window.location.href = '/profile/?tab=subscription';
    return;
  }

  const planKey = anchor.dataset.subscribePlan;
  const billingInterval = (anchor.dataset.billingInterval as 'monthly' | 'yearly') || 'monthly';
  if (!planKey) {
    return;
  }

  anchor.setAttribute('aria-busy', 'true');

  try {
    const session = await billingApi.createCheckoutSession({ planKey, billingInterval });
    billingApi.clearPendingCheckout();
    billingApi.storePendingBillingAction({ kind: 'checkout', planKey, billingInterval });
    window.location.href = session.url;
  } catch (err: any) {
    setResumeOverlayVisible(false);
    billingApi.clearPendingBillingAction();
    toastService.error(err?.message || t('billing.checkoutError'));
  } finally {
    anchor.removeAttribute('aria-busy');
  }
}

function startTrial(overview: BillingOverviewDto | null): void {
  if (!authService.isAuthenticated) {
    authService.rememberPostLoginRedirect('/onboarding/');
    window.location.href = '/login/';
    return;
  }

  if (overview?.subscription.hasManageableSubscription) {
    window.location.href = '/dashboard/';
    return;
  }

  if (overview?.trialExpired) {
    window.location.href = '/profile/?tab=subscription';
    return;
  }

  window.location.href = '/onboarding/';
}

function syncBillingToggle(container: HTMLElement): void {
  let activeBilling: 'monthly' | 'yearly' = (container.dataset.activeBilling as 'monthly' | 'yearly') || 'monthly';

  container.querySelectorAll<HTMLButtonElement>('.pricing-toggle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      activeBilling = (btn.dataset.billing as 'monthly' | 'yearly') || 'monthly';
      applyBillingInterval(container, activeBilling);
    });
  });

  applyBillingInterval(container, activeBilling);
}

async function wireCheckoutButtons(container: HTMLElement): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const recommendedPlan = params.get('recommendedPlan');
  if (params.get('billing') === 'cancelled') {
    billingApi.clearPendingBillingAction();
    toastService.warning(t('billing.checkoutCancelled'));
  }

  if (recommendedPlan) {
    container.querySelector<HTMLElement>(`.pricing-card[data-plan-card="${recommendedPlan}"]`)?.classList.add('pricing-card-current');
  }

  if (authService.isAuthenticated && params.get('providerBoundary') !== '1' && billingApi.getPendingBillingAction()) {
    redirectToBillingProcessing();
    return;
  }

  let hasManageableSubscription = false;
  let billingOverview: BillingOverviewDto | null = null;
  let billingOverviewLoaded = !authService.isAuthenticated;

  const loadBillingOverview = async (): Promise<void> => {
    if (!authService.isAuthenticated || billingOverviewLoaded) {
      return;
    }

    billingOverviewLoaded = true;
    try {
      billingOverview = await billingApi.getOverview();
      hasManageableSubscription = billingOverview.subscription.hasManageableSubscription;
      const currentPlan = billingOverview.subscription.planKey;
      if (currentPlan) {
        const currentCard = container.querySelector<HTMLElement>(`.pricing-card[data-plan-card="${currentPlan}"]`);
        currentCard?.classList.add('pricing-card-current');
      }
    } catch {
      // Keep checkout available; the API will validate on submit.
    }
  };

  const initialBillingOverviewLoad = loadBillingOverview();

  container.querySelectorAll<HTMLAnchorElement>('[data-subscribe-plan]').forEach(anchor => {
    anchor.addEventListener('click', async (event) => {
      if (!authService.isAuthenticated) {
        event.preventDefault();
        const planKey = anchor.dataset.subscribePlan;
        const billingInterval = (anchor.dataset.billingInterval as 'monthly' | 'yearly') || 'monthly';
        if (planKey) {
          billingApi.storePendingCheckout({ planKey, billingInterval });
        }
        window.location.href = '/login/?checkout=resume';
        return;
      }

      event.preventDefault();
      await loadBillingOverview();
      await startCheckout(anchor, hasManageableSubscription);
    });
  });

  container.querySelectorAll<HTMLAnchorElement>('[data-start-trial]').forEach(anchor => {
    anchor.addEventListener('click', async (event) => {
      event.preventDefault();
      await loadBillingOverview();
      startTrial(billingOverview);
    });
  });

  if (authService.isAuthenticated && params.get('checkout') === 'resume') {
    if (!authService.currentUser?.onboardingCompleted) {
      authService.rememberPostOnboardingRedirect('/pricing/?checkout=resume');
      setResumeOverlayVisible(false);
      return;
    }

    await initialBillingOverviewLoad;
    const pendingCheckout = billingApi.getPendingCheckout();
    if (!pendingCheckout) {
      setResumeOverlayVisible(false);
      return;
    }

    applyBillingInterval(container, pendingCheckout.billingInterval);

    const pendingAnchor = container.querySelector<HTMLAnchorElement>(
      `[data-subscribe-plan="${pendingCheckout.planKey}"][data-billing-interval="${pendingCheckout.billingInterval}"]`,
    );

    if (!pendingAnchor) {
      billingApi.clearPendingCheckout();
      setResumeOverlayVisible(false);
      return;
    }

    await startCheckout(pendingAnchor, hasManageableSubscription);
    return;
  }

  await initialBillingOverviewLoad;
  setResumeOverlayVisible(false);
}

export async function initPricingPage(): Promise<void> {
  if (initialized) return;

  const container = document.querySelector<HTMLElement>('[data-pricing-page]');
  if (!container) return;

  initialized = true;
  syncBillingToggle(container);
  onLocaleChange(() => {
    if (cachedPublicPlans.length === 0) {
      return;
    }

    renderPublicPlans(container, cachedPublicPlans);
    applyBillingInterval(container, (container.dataset.activeBilling as 'monthly' | 'yearly') || 'monthly');
  });
  void loadPublicPlans(container);
  await wireCheckoutButtons(container);
}

void initPricingPage();
